import React, { useState, useEffect } from 'react';
import { 
  ChartBarSquareIcon,
  BuildingOfficeIcon,
  BuildingOffice2Icon,
  Squares2X2Icon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { organizationAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

const OrganizationHierarchy = () => {
  const [hierarchyData, setHierarchyData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [viewType, setViewType] = useState('tree'); // tree, list
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadHierarchy();
    }
  }, [selectedCompany]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [companiesResponse] = await Promise.all([
        organizationAPI.getCompanies(),
      ]);

      if (companiesResponse.data.success) {
        setCompanies(companiesResponse.data.data);
        if (companiesResponse.data.data.length > 0 && !selectedCompany) {
          setSelectedCompany(companiesResponse.data.data[0].id);
        }
      }

      await loadHierarchy();
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const loadHierarchy = async () => {
    try {
      const params = {};
      if (selectedCompany) params.company_id = selectedCompany;
      
      const response = await organizationAPI.getOrganizationalHierarchy(params);
      if (response.data.success) {
        setHierarchyData(response.data.data);
        
        // Auto-expand first level nodes
        const firstLevelNodes = new Set();
        response.data.data.forEach(item => {
          if (item.company_id) firstLevelNodes.add(`company-${item.company_id}`);
          if (item.department_id) firstLevelNodes.add(`department-${item.department_id}`);
        });
        setExpandedNodes(firstLevelNodes);
      }
    } catch (err) {
      console.error('Error loading hierarchy:', err);
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Organize data into a hierarchical structure
  const organizeHierarchyData = (data) => {
    const organized = {};
    
    data.forEach(item => {
      const companyKey = item.company_id;
      const departmentKey = item.department_id;
      const divisionKey = item.division_id;
      
      if (!organized[companyKey]) {
        organized[companyKey] = {
          company: {
            id: item.company_id,
            name: item.company_name,
            code: item.company_code
          },
          departments: {},
          unassignedEmployees: []
        };
      }
      
      if (departmentKey) {
        if (!organized[companyKey].departments[departmentKey]) {
          organized[companyKey].departments[departmentKey] = {
            department: {
              id: item.department_id,
              name: item.department_name,
              code: item.department_code
            },
            divisions: {},
            employees: []
          };
        }
        
        if (divisionKey) {
          if (!organized[companyKey].departments[departmentKey].divisions[divisionKey]) {
            organized[companyKey].departments[departmentKey].divisions[divisionKey] = {
              division: {
                id: item.division_id,
                name: item.division_name,
                code: item.division_code
              },
              employees: []
            };
          }
          
          if (item.employee_id) {
            organized[companyKey].departments[departmentKey].divisions[divisionKey].employees.push({
              id: item.employee_id,
              employee_number: item.employee_number,
              first_name: item.first_name,
              last_name: item.last_name,
              job_title: item.job_title,
              hierarchy_level: item.hierarchy_level || 1,
              is_manager: item.is_manager,
              is_department_head: item.is_department_head,
              is_division_head: item.is_division_head,
              position_title: item.position_title,
              position_type: item.position_type,
              organizational_role: item.organizational_role_name,
              can_approve_workflows: item.can_approve_workflows,
              is_management_role: item.is_management_role,
              is_executive_role: item.is_executive_role,
              is_director_role: item.is_director_role
            });
          }
        } else if (item.employee_id) {
          // Employee is in department but not in a division
          organized[companyKey].departments[departmentKey].employees.push({
            id: item.employee_id,
            employee_number: item.employee_number,
            first_name: item.first_name,
            last_name: item.last_name,
            job_title: item.job_title,
            hierarchy_level: item.hierarchy_level || 1,
            is_manager: item.is_manager,
            is_department_head: item.is_department_head,
            is_division_head: item.is_division_head,
            position_title: item.position_title,
            position_type: item.position_type,
            organizational_role: item.organizational_role_name,
            can_approve_workflows: item.can_approve_workflows,
            is_management_role: item.is_management_role,
            is_executive_role: item.is_executive_role,
            is_director_role: item.is_director_role
          });
        }
      } else if (item.employee_id) {
        // Employee is not assigned to any department (unassigned)
        organized[companyKey].unassignedEmployees.push({
          id: item.employee_id,
          employee_number: item.employee_number,
          first_name: item.first_name,
          last_name: item.last_name,
          job_title: item.job_title,
          hierarchy_level: item.hierarchy_level || 1,
          is_manager: item.is_manager,
          is_department_head: item.is_department_head,
          is_division_head: item.is_division_head,
          position_title: item.position_title,
          position_type: item.position_type,
          organizational_role: item.organizational_role_name,
          can_approve_workflows: item.can_approve_workflows,
          is_management_role: item.is_management_role,
          is_executive_role: item.is_executive_role,
          is_director_role: item.is_director_role
        });
      }
    });
    
    // Sort employees by hierarchy level (higher levels first) and then by name
    Object.values(organized).forEach(company => {
      Object.values(company.departments).forEach(dept => {
        dept.employees.sort((a, b) => {
          if (a.hierarchy_level !== b.hierarchy_level) {
            return (b.hierarchy_level || 1) - (a.hierarchy_level || 1);
          }
          return (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name);
        });
        
        Object.values(dept.divisions).forEach(div => {
          div.employees.sort((a, b) => {
            if (a.hierarchy_level !== b.hierarchy_level) {
              return (b.hierarchy_level || 1) - (a.hierarchy_level || 1);
            }
            return (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name);
          });
        });
      });
      
      company.unassignedEmployees.sort((a, b) => {
        if (a.hierarchy_level !== b.hierarchy_level) {
          return (b.hierarchy_level || 1) - (a.hierarchy_level || 1);
        }
        return (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name);
      });
    });
    
    return organized;
  };

  if (loading) return <LoadingSpinner />;

  const organizedData = organizeHierarchyData(hierarchyData);
  const selectedCompanyData = companies.find(c => c.id === selectedCompany);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organization Hierarchy</h1>
          <p className="text-gray-600">Visual representation of organizational structure</p>
        </div>
        <div className="flex items-center space-x-4">
          {companies.length > 1 && (
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          )}
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewType('tree')}
              className={`px-4 py-2 text-sm font-medium border ${
                viewType === 'tree'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              Tree View
            </button>
            <button
              onClick={() => setViewType('list')}
              className={`px-4 py-2 text-sm font-medium border-t border-r border-b ${
                viewType === 'list'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-r-md focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              List View
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {selectedCompanyData && (
        <div className="bg-white shadow rounded-lg">
          {viewType === 'tree' ? (
            <TreeView 
              data={organizedData} 
              selectedCompany={selectedCompanyData}
              expandedNodes={expandedNodes}
              onToggleNode={toggleNode}
            />
          ) : (
            <ListView 
              data={hierarchyData} 
              selectedCompany={selectedCompanyData}
            />
          )}
        </div>
      )}

      {hierarchyData.length === 0 && (
        <div className="text-center py-12 bg-white shadow rounded-lg">
          <ChartBarSquareIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No organizational data</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create companies, departments, divisions, and assign employees to build your organizational hierarchy.
          </p>
        </div>
      )}
    </div>
  );
};

const TreeView = ({ data, selectedCompany, expandedNodes, onToggleNode }) => {
  const companyData = data[selectedCompany.id];

  if (!companyData) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">No data available for selected company</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Company Level */}
      <div className="flex items-center space-x-3 mb-6">
        <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
        <div>
          <h2 className="text-xl font-bold text-gray-900">{companyData.company.name}</h2>
          <p className="text-sm text-gray-500">Code: {companyData.company.code}</p>
        </div>
      </div>

      {/* Departments */}
      <div className="ml-8 space-y-6">
        {Object.entries(companyData.departments).map(([deptId, deptData]) => {
          const deptNodeId = `department-${deptId}`;
          const isDeptExpanded = expandedNodes.has(deptNodeId);
          const hasDivisions = Object.keys(deptData.divisions).length > 0;
          const hasEmployees = deptData.employees.length > 0;

          return (
            <div key={deptId} className="border-l-2 border-gray-200 pl-6">
              <div className="flex items-center justify-between">
                <div 
                  className="flex items-center space-x-3 cursor-pointer"
                  onClick={() => onToggleNode(deptNodeId)}
                >
                  <BuildingOffice2Icon className="h-6 w-6 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{deptData.department.name}</h3>
                    <p className="text-sm text-gray-500">Code: {deptData.department.code}</p>
                  </div>
                  {(hasDivisions || hasEmployees) && (
                    <button className="text-gray-400 hover:text-gray-600">
                      <svg
                        className={`h-5 w-5 transform transition-transform ${
                          isDeptExpanded ? 'rotate-90' : ''
                        }`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>{Object.keys(deptData.divisions).length} divisions</span>
                  <span>{deptData.employees.length + Object.values(deptData.divisions).reduce((sum, div) => sum + div.employees.length, 0)} employees</span>
                </div>
              </div>

              {isDeptExpanded && (
                <div className="mt-4 ml-8 space-y-4">
                  {/* Divisions */}
                  {Object.entries(deptData.divisions).map(([divId, divData]) => (
                    <div key={divId} className="border-l-2 border-gray-100 pl-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Squares2X2Icon className="h-5 w-5 text-green-600" />
                          <div>
                            <h4 className="font-medium text-gray-900">{divData.division.name}</h4>
                            <p className="text-sm text-gray-500">Code: {divData.division.code}</p>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {divData.employees.length} employees
                        </div>
                      </div>

                      {/* Division Employees */}
                      {divData.employees.length > 0 && (
                        <div className="mt-2 ml-8 space-y-2">
                          {divData.employees.map((employee) => (
                            <EmployeeCard key={employee.id} employee={employee} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Department Employees (not in divisions) */}
                  {deptData.employees.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-gray-700">Department Employees</h5>
                      <div className="ml-6 space-y-2">
                        {deptData.employees.map((employee) => (
                          <EmployeeCard key={employee.id} employee={employee} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        
        {/* Unassigned Employees */}
        {companyData.unassignedEmployees && companyData.unassignedEmployees.length > 0 && (
          <div className="mt-6 border-l-2 border-gray-200 pl-6">
            <div className="flex items-center space-x-3 mb-4">
              <UsersIcon className="h-6 w-6 text-gray-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Unassigned Employees</h3>
                <p className="text-sm text-gray-500">Employees not assigned to any department</p>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>{companyData.unassignedEmployees.length} employees</span>
              </div>
            </div>
            <div className="ml-8 space-y-2">
              {companyData.unassignedEmployees.map((employee) => (
                <EmployeeCard key={employee.id} employee={employee} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ListView = ({ data, selectedCompany }) => {
  const employeesWithData = data
    .filter(item => item.employee_id)
    .sort((a, b) => {
      // Sort by hierarchy level (highest first), then by department, then by name
      if ((a.hierarchy_level || 1) !== (b.hierarchy_level || 1)) {
        return (b.hierarchy_level || 1) - (a.hierarchy_level || 1);
      }
      if (a.department_name !== b.department_name) {
        return (a.department_name || '').localeCompare(b.department_name || '');
      }
      return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
    });

  return (
    <div className="p-6">
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Employee Directory</h3>
          <div className="text-sm text-gray-500">
            {employeesWithData.length} employees
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Position
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Division
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Organizational Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Capabilities
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {employeesWithData.map((item) => (
              <tr key={item.employee_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-700">
                          {item.first_name?.[0]}{item.last_name?.[0]}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {item.first_name} {item.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.employee_number}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{item.job_title || 'Not specified'}</div>
                  {item.position_title && item.position_title !== item.job_title && (
                    <div className="text-sm text-gray-500">{item.position_title}</div>
                  )}
                  {item.position_type && (
                    <div className="text-xs text-gray-400 capitalize">{item.position_type}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.department_name || 'Unassigned'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.division_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">Level {item.hierarchy_level || 1}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {item.organizational_role_name || 'Not assigned'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {item.is_department_head && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Dept Head
                      </span>
                    )}
                    {item.is_division_head && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Div Head
                      </span>
                    )}
                    {item.is_manager && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Manager
                      </span>
                    )}
                    {item.can_approve_workflows && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        Approver
                      </span>
                    )}
                    {item.is_executive_role && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Executive
                      </span>
                    )}
                    {item.is_director_role && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Director
                      </span>
                    )}
                    {item.is_management_role && !item.is_manager && !item.is_director_role && !item.is_executive_role && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Management
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {employeesWithData.length === 0 && (
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No employees found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No employee records are available for the selected company.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const EmployeeCard = ({ employee }) => {
  return (
    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
      <div className="flex-shrink-0">
        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
          <span className="text-xs font-medium text-primary-700">
            {employee.first_name?.[0]}{employee.last_name?.[0]}
          </span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {employee.first_name} {employee.last_name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {employee.employee_number} • {employee.job_title || 'No job title'}
            </p>
            {employee.organizational_role && (
              <p className="text-xs text-blue-600 font-medium truncate">{employee.organizational_role}</p>
            )}
          </div>
          <div className="flex items-center space-x-2 ml-2">
            <span className="text-xs text-gray-500 whitespace-nowrap">Level {employee.hierarchy_level || 1}</span>
          </div>
        </div>
        {employee.position_title && employee.position_title !== employee.job_title && (
          <p className="text-xs text-gray-400 mt-1 truncate">{employee.position_title}</p>
        )}
        <div className="flex flex-wrap gap-1 mt-1">
          {employee.can_approve_workflows && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Approver
            </span>
          )}
          {employee.is_department_head && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Dept Head
            </span>
          )}
          {employee.is_division_head && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Div Head
            </span>
          )}
          {employee.is_manager && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              Manager
            </span>
          )}
          {employee.is_executive_role && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Executive
            </span>
          )}
          {employee.is_director_role && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              Director
            </span>
          )}
          {employee.is_management_role && !employee.is_manager && !employee.is_director_role && !employee.is_executive_role && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Management
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationHierarchy;